import React, { useContext, useMemo } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { Link as RouterLink, Navigate, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { LayoutUsers } from './LayoutUsers';
import { LayoutAnalyticProfiles } from './LayoutAnalyticProfiles';
import CompanyDescriptionForm from '../components/analytic-profiles/CompanyDescriptionForm';
import { PublishVenuesList } from '../components/publish-venues/PublishVenuesList';
import { ModelsList } from '../components/agent-studio/ModelsList';
import { SkillsList } from '../components/agent-studio/SkillsList';
import { ToolsList } from '../components/agent-studio/ToolsList';
import { ApplicationsPage } from '../components/applications/ApplicationsPage';
import { AdminSection, adminPath, parseAppLocation } from '../routing/appRoutes';
import { ignorePlainClick } from '../routing/linkClick';

/**
 * Admin area: Users, Analytic Profiles, Company Profile, and Publish Venues under the main "Admin" tab.
 */
export const LayoutAdmin = () => {
  const { user } = useContext(UserContext);
  const canUsers = Boolean(user?.permissions?.users?.read || user?.permissions?.users?.create);
  const canProfiles = Boolean(
    user?.permissions?.analytic_profiles?.read || user?.permissions?.analytic_profiles?.create
  );
  const canPublishVenues = Boolean(
    user?.permissions?.analytic_profiles?.read || user?.permissions?.analytic_profiles?.create
  );
  const canCompany = Boolean(
    user?.permissions?.company_profile?.read || user?.permissions?.company_profile?.update
  );

  const subTabs = useMemo(() => {
    const tabs: { key: AdminSection; label: string; content: React.ReactNode }[] = [];
    if (canUsers) {
      tabs.push({
        key: 'users',
        label: 'Users',
        content: <LayoutUsers showPageTitle={false} />,
      });
    }
    if (canProfiles) {
      tabs.push({
        key: 'analytic-profiles',
        label: 'Analytic Profiles',
        content: <LayoutAnalyticProfiles hideCompanyTab />,
      });
    }
    if (canCompany) {
      tabs.push({
        key: 'company',
        label: 'Company Profile',
        content: (
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 4 }}>
            <CompanyDescriptionForm />
          </Box>
        ),
      });
    }
    if (canPublishVenues) {
      tabs.push({
        key: 'publish-venues',
        label: 'Publish Venues',
        content: (
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 4 }}>
            <PublishVenuesList />
          </Box>
        ),
      });
    }
    tabs.push({
      key: 'models',
      label: 'Models',
      content: (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 4 }}>
          <ModelsList />
        </Box>
      ),
    });
    tabs.push({
      key: 'skills',
      label: 'Skills',
      content: (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 4 }}>
          <SkillsList />
        </Box>
      ),
    });
    tabs.push({
      key: 'tools',
      label: 'Tools',
      content: (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 4 }}>
          <ToolsList />
        </Box>
      ),
    });
    tabs.push({
      key: 'applications',
      label: 'Applications',
      content: (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 4 }}>
          <ApplicationsPage embedded />
        </Box>
      ),
    });
    return tabs;
  }, [canUsers, canProfiles, canPublishVenues, canCompany]);

  // The section comes from the URL (/settings/<section>); bare /settings shows the first permitted one.
  const location = useLocation();
  const section = parseAppLocation(location.pathname)?.section;
  const sectionIndex = subTabs.findIndex((t) => t.key === section);

  if (subTabs.length === 0) {
    return null;
  }
  if (section && sectionIndex < 0) {
    // Section the user can't access (or no longer has): fall back to the first permitted one.
    return <Navigate to={adminPath()} replace />;
  }
  const subTab = Math.max(0, sectionIndex);

  return (
    <Box sx={{ bgcolor: '#0a0a0a', minHeight: 'calc(100vh - 200px)' }}>
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, borderBottom: '1px solid #27272a' }}>
        <Tabs
          value={subTab}
          aria-label="Admin sections"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              color: '#a1a1aa',
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.9375rem',
              fontWeight: 500,
              '&.Mui-selected': {
                color: '#fafafa',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#667eea',
            },
          }}
        >
          {subTabs.map((t, i) => (
            // Router links (no Tabs onChange): modified clicks open a new tab without switching this one.
            <Tab
              key={t.label}
              label={t.label}
              component={RouterLink}
              to={adminPath(t.key)}
              onClick={i === subTab ? ignorePlainClick : undefined}
            />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ flexGrow: 1 }}>{subTabs[subTab]?.content}</Box>
    </Box>
  );
};
